import { useMemo } from "react";
import type { ReportColumn, ResultCell } from "../../../api/portalReportsApi";
import { formatCell } from "./formatCell";

export type FilterState = Record<string, string | null>;

/** Rows matching every active chip filter. */
export function applyFilters(rows: ResultCell[][], colIndex: Record<string, number>, filters: FilterState): ResultCell[][] {
  const active = Object.entries(filters).filter(([, v]) => v != null && v !== "");
  if (active.length === 0) return rows;
  return rows.filter((r) => active.every(([col, v]) => colIndex[col] != null && String(r[colIndex[col]] ?? "") === v));
}

/**
 * Chip rows per declared filter column: "All" plus every distinct value in
 * the (unfiltered) result. Filtering happens in the browser, no re-run.
 */
export function SectionFilters({
  filterColumns,
  columns,
  colIndex,
  rows,
  filters,
  onChange,
}: {
  filterColumns: string[];
  columns: ReportColumn[];
  colIndex: Record<string, number>;
  rows: ResultCell[][];
  filters: FilterState;
  onChange: (next: FilterState) => void;
}) {
  const distinct = useMemo(() => {
    const out: Record<string, { value: string; label: string }[]> = {};
    for (const fc of filterColumns) {
      const idx = colIndex[fc];
      if (idx == null) continue;
      const col = columns.find((c) => c.name === fc);
      const seen = new Map<string, string>();
      for (const r of rows) {
        const raw = r[idx];
        const key = String(raw ?? "");
        if (!seen.has(key)) seen.set(key, raw == null || raw === "" ? "(empty)" : formatCell(raw, col));
      }
      out[fc] = [...seen.entries()].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
    }
    return out;
  }, [filterColumns, colIndex, rows, columns]);

  if (filterColumns.length === 0) return null;
  return (
    <div style={{ display: "grid", gap: 6 }}>
      {filterColumns.map((fc) => {
        const col = columns.find((c) => c.name === fc);
        const opts = distinct[fc] ?? [];
        if (opts.length === 0) return null;
        const current = filters[fc] ?? null;
        return (
          <div key={fc} style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", fontSize: "0.8rem" }}>
            <span style={{ color: "var(--portal-muted)", marginRight: 4 }}>{col?.label || fc}:</span>
            <Chip active={current == null} onClick={() => onChange({ ...filters, [fc]: null })}>All</Chip>
            {opts.slice(0, 40).map((o) => (
              <Chip key={o.value} active={current === o.value} onClick={() => onChange({ ...filters, [fc]: o.value })}>
                {o.label}
              </Chip>
            ))}
            {opts.length > 40 && <span style={{ color: "var(--portal-muted)" }}>+{opts.length - 40} more</span>}
          </div>
        );
      })}
    </div>
  );
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        padding: "3px 10px",
        borderRadius: 999,
        border: `1px solid ${active ? "var(--portal-primary)" : "var(--portal-border)"}`,
        background: active ? "var(--portal-primary-soft)" : "white",
        color: active ? "var(--portal-primary-darker)" : "var(--portal-text)",
        font: "inherit",
        fontSize: "0.8rem",
        fontWeight: active ? 700 : 500,
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}
